import React, { useState } from 'react';

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  TextField,
  Typography,
} from '@mui/material';

import { KanbanItem, Objective } from '../../@types/State';
import { effects } from '../../Effects';
import { letterize } from '../../helpers/NumberFormatting';

const metrics = [
  'Acres',
  'Peons',
  'Horses',
  'Gold',
  'Food',
  'Runes',
  'OP count',
];

const opTypes = Object.entries(effects)
  .filter(
    ([key, value]) =>
      value.isBad === value.isRequestable && !value.isDuration && value.isBad
  )
  .map(([key, value]) => key);

export const followersMarks = [
  {
    value: 0,
    scaledValue: 0,
    label: '0',
  },
  {
    value: 25,
    scaledValue: 250,
    label: '250',
  },
  {
    value: 50,
    scaledValue: 500,
    label: '500',
  },
  {
    value: 75,
    scaledValue: 750,
    label: '750',
  },
  {
    value: 100,
    scaledValue: 1000,
    label: '1k',
  },
  {
    value: 125,
    scaledValue: 2000,
    label: '2k',
  },
  {
    value: 150,
    scaledValue: 2500,
    label: '2.5k',
  },
  {
    value: 175,
    scaledValue: 5000,
    label: '5k',
  },
  {
    value: 200,
    scaledValue: 10000,
    label: '10k',
  },
  {
    value: 225,
    scaledValue: 20000,
    label: '20k',
  },
];

const scale = (value: number) => {
  const previousMarkIndex = Math.floor(value / 25);
  const previousMark = followersMarks[previousMarkIndex];
  const remainder = value % 25;
  if (remainder === 0) {
    return previousMark.scaledValue;
  }
  const nextMark = followersMarks[previousMarkIndex + 1];
  const increment = (nextMark.scaledValue - previousMark.scaledValue) / 25;
  return remainder * increment + previousMark.scaledValue;
};

interface Props {
  addObjective: (objective: Objective, selected: KanbanItem | null) => void;
  addOp: (op: string, selected: KanbanItem | null) => void;
  selected: KanbanItem | null;
}

const ObjectiveSelector: React.FC<Props> = ({
  addObjective,
  addOp,
  selected,
}) => {
  const [metric, setMetric] = useState(metrics[0]);
  const [metricValue, setMetricValue] = useState(1);
  const [opType, setOpType] = useState(opTypes[0]);
  const [opCount, setOpCount] = useState(0);
  const handleChange = (event: SelectChangeEvent) => {
    setMetric(event.target.value);
  };

  const handleOpTypeChange = (event: SelectChangeEvent) => {
    setOpType(event.target.value);
  };

  const handleValueChange = (event: Event, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setMetricValue(newValue);
    }
  };

  const handleAddClick = () => {
    if (isOpType) {
      addObjective(
        {
          type: opType,
          value: opCount,
        } as Objective,
        selected
      );
      addOp(opType, selected);
    } else {
      addObjective(
        {
          type: metric,
          value: scale(metricValue),
        } as Objective,
        selected
      );
    }
  };

  const isOpType = metric === 'OP count';
  return (
    <Box>
      <Typography variant='body1' textAlign='center'>
        Add an Objective
      </Typography>
      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel id='metric-select-label'>Metric</InputLabel>
        <Select
          labelId='metric-select-label'
          id='metric-select'
          value={metric}
          label='Metric'
          size='small'
          onChange={handleChange}
        >
          {metrics.map((x) => (
            <MenuItem key={x} value={x}>
              {x}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {!isOpType && (
        <Box my={2} mx={1}>
          <Slider
            style={{ maxWidth: 500 }}
            value={metricValue}
            min={0}
            step={1}
            max={225}
            valueLabelFormat={letterize}
            marks={followersMarks}
            scale={scale}
            onChange={handleValueChange}
            valueLabelDisplay='auto'
            aria-labelledby='non-linear-slider'
          />
          <Typography variant='body2' gutterBottom sx={{ textAlign: 'left' }}>
            Target {metric}Amount: {letterize(scale(metricValue))}{' '}
          </Typography>
        </Box>
      )}
      {isOpType && (
        <Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id='metric-select-label'>Op Type</InputLabel>
            <Select
              labelId='metric-select-label'
              id='metric-select'
              value={opType}
              label='Op Type'
              size='small'
              onChange={handleOpTypeChange}
            >
              {opTypes.map((x) => (
                <MenuItem key={x} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{ mt: 2 }}
            fullWidth
            name='op-count'
            size='small'
            type='number'
            label='count of op'
            InputProps={{ inputProps: { min: 1, max: 25 } }}
            value={opCount}
            onChange={(e) => setOpCount(Number(e.target.value))}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 2,
        }}
      >
        <Button
          variant='outlined'
          onClick={handleAddClick}
          sx={{ ml: 2 }}
          size='small'
        >
          Add
        </Button>
      </Box>
    </Box>
  );
};

export default ObjectiveSelector;
